<template>
  <div>
    <BModal
      id="modal-topup-balance-monit"
      no-stacking
      centered
      hide-footer
      hide-header
      hide-header-close
      no-close-on-backdrop
      no-close-on-esc
      size="md"
    >
      <BRow class="justify-center my-1 flex flex-column">
        <BCol
          class="flex flex-col text-center gap-4 justify-content-center mb-2"
        >
          <strong class="text-black text-xl">
            Topup Saldo Monit
          </strong>
          <span class="text-black text-md">
            Masukkan nominal topup dan VA bank tujuan kamu, dan pastikan VA
            sesuai yang kamu tuju
          </span>
        </BCol>
        <BCol class="justify-content-center">
          <ValidationObserver #default="{ invalid }">
            <div
              class="mb-1 !border !border-solid !border-[#F95031] bg-[#FCD4BE] rounded-lg py-1 ps-4"
            >
              <div class="flex flex-row">
                <span
                  class="font-semibold text-md block text-black"
                >Saldo Xendit : {{ IDR(balance) }}</span>
              </div>
            </div>

            <BFormGroup label-for="nominal">
              <template #label>
                <div class="flex justify-between">
                  <div class="text-black flex items-center">
                    <strong class="text-md font-semibold">Nominal</strong>
                  </div>
                </div>
              </template>
              <ValidationProvider
                v-slot="{ errors }"
                name="Nominal"
                :rules="{
                  required: true,
                  min_value: 10000,
                  max_value: balance || 999999999,
                }"
                label-for="nominal"
                label-slot="label-nominal"
              >
                <BFormInput
                  v-model="nominal"
                  type="text"
                  maxlength="14"
                  placeholder="minimal Rp10.000,-, maksimal Rp999.999.999,-"
                  @keypress="isNumber($event)"
                  @input="formatNumber"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </ValidationProvider>
            </BFormGroup>

            <BFormGroup label-for="virtual-account">
              <template #label>
                <div class="flex justify-between">
                  <div class="text-black flex items-center">
                    <strong
                      class="text-md font-semibold"
                    >Nomor VA Tujuan
                    </strong>
                  </div>
                </div>
              </template>
              <ValidationProvider
                v-slot="{ errors }"
                name="Virtual Account"
              >
                <VSelect
                  v-model="selectedAccount"
                  label="account_number"
                  :options="billAccounts"
                  placeholder="Pilih virtual account tujuan"
                >
                  <template #no-options>
                    Tidak ada virtual account tersedia
                  </template>
                  <template #option="option">
                    {{ option.bank_code }} - {{ option.account_number }} -
                    {{ option.account_holder_name }}
                  </template>
                  <template #selected-option="option">
                    {{ option.bank_code }} - {{ option.account_number }} -
                    {{ option.account_holder_name }}
                  </template>
                </VSelect>
                <small class="text-danger">{{ errors[0] }}</small>
              </ValidationProvider>
              <span
                class="mt-1 text-sm"
              >Pastikan Rekening yang kamu pilih sudah benar</span>
            </BFormGroup>

            <div class="mt-2 flex cols cols-12 gap-4">
              <BButton
                variant="outline-primary"
                class="rounded-lg px-4 w-full cols-6"
                @click="$bvModal.hide('modal-topup-balance-monit'), resetData()"
              >
                Batal
              </BButton>

              <BButton
                :variant="
                  invalid || !selectedAccount || !nominal
                    ? 'secondary'
                    : 'primary'
                "
                class="rounded-lg px-4 bg-[#FF5C00] border-[#FF5C00] w-full cols-6"
                :disabled="invalid || !selectedAccount || !nominal"
                @click="submitTopup"
              >
                Submit
              </BButton>
            </div>
          </ValidationObserver>
        </BCol>
      </BRow>
    </BModal>
  </div>
</template>

<script>
import { ValidationProvider, ValidationObserver, extend } from 'vee-validate'
import { IDR } from '@/libs/currency'
import { axiosIns, newAxiosIns } from '@/libs/axios'
import VSelect from 'vue-select'
import 'vue-select/dist/vue-select.css'
import { isNumber } from '@/libs/helpers'
import { required } from '@validations'

extend('min_value', {
  validate: (value, { min_value }) => {
    const numValue = parseFloat(value.replace(/[^0-9,-]+/g, ''))
    // eslint-disable-next-line camelcase
    return numValue >= min_value
  },
  params: ['min_value'],
  message: (_, { min_value }) => `Minimal topup ${IDR(min_value)}`,
})

extend('max_value', {
  validate: (value, { max_value }) => {
    const numValue = parseFloat(value.replace(/[^0-9,-]+/g, ''))
    // eslint-disable-next-line camelcase
    return numValue <= max_value
  },
  params: ['max_value'],
  message: (_, { max_value }) => `Maksimal topup ${IDR(max_value)}`,
})

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    VSelect,
  },
  data() {
    return {
      IDR,
      isNumber,

      required,

      balance: null,

      nominal: '',
      selectedAccount: null,

      loading: {
        balance: false,
        account: false,
      },
      billAccounts: [],
    }
  },
  watch: {
    nominal(newValue) {
      if (newValue === null) {
        this.$nextTick(() => {
          this.nominal = 'Rp '
        })
      }
      const result = newValue
        .replace(/\D/g, '')
        .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      this.$nextTick(() => {
        this.nominal = `Rp ${result}`
      })
    },
  },
  mounted() {
    this.getDataXendit()
    this.getBillAccounts()
  },
  methods: {
    formatNumber(value) {
      // eslint-disable-next-line radix
      const result = parseInt(value.replace(/[^0-9]/g, ''))
      return result
    },

    /**
     * Get Xendit balance
     *
     */
    async getDataXendit() {
      this.loading.balance = true
      await axiosIns.get('/v1/admin/withdrawal/balance/xendit').then(res => {
        const { data } = res.data
        this.balance = data
        this.loading.balance = false
      })
    },

    /**
     * Get bill accounts
     *
     */
    async getBillAccounts() {
      this.loading.account = true
      await newAxiosIns
        .get('/komcards/api/v1/admin/monit/destination-accounts')
        .then(res => {
          const { data } = res.data
          this.billAccounts = data
        })
        .finally(() => {
          this.loading.account = false
        })
    },

    submitTopup() {
      const payload = {
        nominal: this.formatNumber(this.nominal.replace(/[^0-9,-]+/g, '')),
        destination_account_id: this.selectedAccount.id,
      }

      this.$emit('topup', payload)
    },

    resetData() {
      this.nominal = ''
      this.selectedAccount = null
    },
  },
}
</script>

<style scoped>
/* Custom VSelect Styling */
.v-select {
  /* Ensure consistent styling with existing form inputs */
  border-radius: 0.375rem;
  transition: all 0.25s ease-in-out;
}

/* VSelect dropdown options styling */
.v-select ::v-deep .vs__dropdown-option {
  padding: 12px 16px;
  color: #22292f;
  background-color: #ffffff;
  border: none;
  transition: all 300ms ease-in-out;
  cursor: pointer;
  font-size: 0.875rem;
  line-height: 1.5;
}

/* Orange hover effect for dropdown options */
.v-select ::v-deep .vs__dropdown-option:hover {
  background-color: #fcd4be;
  color: #000000 !important;
  transform: translateX(2px);
}

/* Selected option styling */
.v-select ::v-deep .vs__dropdown-option--selected {
  background-color: #ff5c00;
  color: #ffffff !important;
  font-weight: 500;
}

/* Selected option hover state */
.v-select ::v-deep .vs__dropdown-option--selected:hover {
  background-color: #fcd4be;
  color: #000000 !important;
}

/* Highlighted option (keyboard navigation) */
.v-select ::v-deep .vs__dropdown-option--highlight {
  background-color: #fcd4be;
  color: #000000 !important;
}

/* Dropdown menu styling */
.v-select ::v-deep .vs__dropdown-menu {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 8px 0;
  max-height: 200px;
  overflow-y: auto;
}

/* Dropdown toggle styling */
.v-select ::v-deep .vs__dropdown-toggle {
  border: 1px solid #222222 !important;
  border-radius: 0.375rem;
  padding: 8px 12px;
  background-color: #ffffff;
  transition: all 300ms ease-in-out;
}

/* Selected option display styling */
.v-select ::v-deep .vs__selected {
  color: #22292f;
  font-weight: 500;
  margin: 0;
}

/* Search input styling */
.v-select ::v-deep .vs__search {
  color: #22292f;
  font-size: 0.875rem;
  margin: 0;
}

.v-select ::v-deep .vs__search::placeholder {
  color: #9ca3af;
}

/* Open indicator (arrow) styling */
.v-select ::v-deep .vs__open-indicator {
  fill: #6b7280;
  transition: transform 300ms ease-in-out;
}

.v-select ::v-deep .vs--open .vs__open-indicator {
  transform: rotate(180deg);
}

.v-select ::v-deep .vs__actions {
  padding: 0;
}

/* No options message styling */
.v-select ::v-deep .vs__no-options {
  color: #6b7280;
  font-style: italic;
  padding: 12px 16px;
  text-align: center;
}

/* Responsive design for mobile devices */
@media (max-width: 768px) {
  .v-select ::v-deep .vs__dropdown-option {
    padding: 14px 16px;
    font-size: 1rem;
  }

  .v-select ::v-deep .vs__dropdown-menu {
    max-height: 180px;
  }

  .v-select ::v-deep .vs__dropdown-toggle {
    padding: 10px 12px;
  }
}

/* Accessibility improvements */
.v-select ::v-deep .vs__dropdown-option:focus {
  outline: 2px solid #ff5c00;
  outline-offset: -2px;
  background-color: #fcd4be;
  color: #000000 !important;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .v-select ::v-deep .vs__dropdown-option:hover,
  .v-select ::v-deep .vs__dropdown-option--highlight {
    background-color: #ff8c00;
    color: #000000;
    border: 2px solid #000000;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .v-select ::v-deep .vs__dropdown-option,
  .v-select ::v-deep .vs__dropdown-toggle,
  .v-select ::v-deep .vs__open-indicator,
  .v-select ::v-deep .vs__clear {
    transition: none;
  }
}
</style>
